import { useRef, useState, Activity, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useLocation } from 'react-router-dom';

import { IconSearch } from '@tabler/icons-react';

import EmptyState from '@/components/EmptyState';
import Input from '@/components/Input/Input';
import Pagination from '@/components/Pagination';
import { ELessonStatus, ELessonStageType } from '@/enum';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import { getTeacherClassLessonBreakDownList } from '@/service/classService';
import { useSkeletonStore } from '@/store/useSkeletonStore';
import { formatQuery } from '@/utils';

import LessonBreakdownCard from './components/LessonBreakdownCard';
import LessonBreakdownListSkeleton from './components/LessonBreakdownListSkeleton';
import TeacherLessonDetailsDrawer from './components/TeacherLessonDetailsDrawer';

interface IClassLessonBreakdown {
  id: string;
  title: string;
  stage: ELessonStageType;
  status: ELessonStatus;
  average_progress?: number;
  assign_date?: number | string;
  due_date?: number | string;
  created_by?: {
    id: string;
    name: string;
  };
}

const LessonBreakdownList = () => {
  const { formatText } = useFormatText();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { setTitle, setIsBreadcrumb } = usePageTitle();
  const { isScreenLoading, setScreenLoading } = useSkeletonStore();

  const pageTitle = formatText('teacher-classes.details.lesson-breakdown');
  const classesTitle = formatText('teacher-classes.title');

  const [lessons, setLessons] = useState<IClassLessonBreakdown[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [className] = useState<string>(location.state?.className ?? '');
  const [selectedLessonId, setSelectedLessonId] = useState<string | null>(null);
  const [isLessonDrawerOpen, setIsLessonDrawerOpen] = useState(false);
  const isInitialLoad = useRef(true);
  const pageSize = 12;

  const { register, watch } = useForm({
    defaultValues: { search: '' },
  });

  const search = watch('search');
  const debouncedSearch = useDebounce(search, 500);

  useEffect(() => {
    setTitle(pageTitle);
    setIsBreadcrumb([
      { label: classesTitle, to: '/teacher/classes' },
      {
        label: className || '...',
        to: id ? `/teacher/classes/${id}` : '/teacher/classes',
      },
      { label: pageTitle },
    ]);

    return () => {
      setTitle('');
      setIsBreadcrumb([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [className, pageTitle, classesTitle]);

  const fetchData = async () => {
    if (isInitialLoad.current) {
      setScreenLoading(true);
    }
    try {
      const params = {
        class_id: id,
        search: debouncedSearch,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
      };

      const query = formatQuery(params);

      const response = await getTeacherClassLessonBreakDownList(query);
      if (response?.status && response?.data) {
        setLessons(response.data ?? []);
        setTotal(response.total ?? 0);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setScreenLoading(false);
      isInitialLoad.current = false;
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, debouncedSearch, currentPage, pageSize]);

  const filteredLessons = lessons;

  const renderContent = () => {
    if (isScreenLoading) {
      return <LessonBreakdownListSkeleton />;
    }

    if (filteredLessons.length > 0) {
      return (
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {filteredLessons.map((lesson) => (
              <LessonBreakdownCard
                key={lesson.id}
                name={lesson.title}
                assignedDate={lesson.assign_date ?? ''}
                dueDate={lesson.due_date ?? ''}
                progress={lesson.average_progress || 0}
                status={lesson.status || ''}
                onClick={() => {
                  setSelectedLessonId(lesson.id);
                  setIsLessonDrawerOpen(true);
                }}
              />
            ))}
          </div>
          {total > pageSize && (
            <Pagination
              total={total}
              currentPage={currentPage}
              pageSize={pageSize}
              setCurrentPage={setCurrentPage}
              className="border-t-0!"
            />
          )}
        </div>
      );
    }

    return (
      <EmptyState title={formatText('common.no-data-found')} className="h-75" />
    );
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Search bar */}
      <div className="flex items-center gap-3 ps-1">
        <Input
          name="search"
          register={register}
          placeholder={formatText(
            'teacher-classes.details.breakdown-search-placeholder'
          )}
          leftIcon={{
            icon: <IconSearch size={20} className="text-neutral-500" />,
          }}
          className="w-full max-w-75"
          inputClassName="py-[13px]"
        />
      </div>

      {renderContent()}

      <Activity
        mode={isLessonDrawerOpen && selectedLessonId ? 'visible' : 'hidden'}
      >
        <TeacherLessonDetailsDrawer
          isOpen={isLessonDrawerOpen}
          onClose={() => {
            setIsLessonDrawerOpen(false);
            setSelectedLessonId(null);
          }}
          lessonId={selectedLessonId}
          onSuccess={fetchData}
        />
      </Activity>
    </div>
  );
};

export default LessonBreakdownList;
